// Treatment-plan entries for a case: list, add (title/notes + optional linked file id from the
// Files tab), and delete. Refreshes off the treatment-plan SSE.
import { useCallback, useEffect, useState } from 'react';
import { type CaseTreatmentPlan, formatDateTime } from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

export function CaseTreatmentPlans({ caseId }: { caseId: string }) {
  const can = useCan();
  const editable = can('case:update');
  const [items, setItems] = useState<CaseTreatmentPlan[]>([]);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [mediaId, setMediaId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get<CaseTreatmentPlan[]>(`/cases/${caseId}/treatment-plans`)
      .then(setItems)
      .catch(() => setItems([]));
  }, [caseId]);

  useEffect(() => load(), [load]);
  useLiveEvent('case.treatment_plan.changed', () => load());

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/treatment-plans`, {
        title: title.trim() || undefined,
        notes: notes.trim() || undefined,
        mediaId: mediaId.trim() || undefined,
      });
      setTitle('');
      setNotes('');
      setMediaId('');
      load();
    } catch {
      setError(t('cases.plans.error'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await api.del(`/cases/${caseId}/treatment-plans/${id}`);
      load();
    } catch {
      setError(t('cases.plans.error'));
    }
  }

  return (
    <div>
      {items.length === 0 ? (
        <p className="mb-4 text-sm text-muted">{t('cases.plans.empty')}</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {items.map((p) => (
            <li key={p.id} className="rounded border border-line bg-surface-2 px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">
                    {t('cases.plans.no')} {p.planNo}
                    {p.title ? ` · ${p.title}` : ''}
                  </p>
                  {p.notes && <p className="mt-1 whitespace-pre-wrap text-sm text-muted">{p.notes}</p>}
                  <p className="mt-1 text-xs text-muted">{formatDateTime(p.createdAt)}</p>
                </div>
                {editable && (
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    className="text-xs text-status-urgent-fg"
                  >
                    {t('cases.plans.delete')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <form className="space-y-3 border-t border-line pt-4" onSubmit={add}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.plans.title')}</label>
              <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.plans.mediaId')}</label>
              <input className={inputClass} value={mediaId} onChange={(e) => setMediaId(e.target.value)} />
            </div>
          </div>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.plans.notes')}</label>
            <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={busy}>
              {t('cases.plans.add')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
